/**
 * Simple localStorage with Cookie Fallback. Checks if localstorage exists and if it works to provide
 * compatibility to iOS/macOS Safar 10 and bellow in incognito mode.
 *
 * Usage:
 * appStorage('key', 'value') - Create a storage with name key and value.
 * appStorage('key') - Returns the value of storage named key.
 * appStorage('key', null) - Removes storage named key.
 */

const appStorage = (key, value) => {
    let lsSupport = false;
    const cookieConfig = { expiration: 30 };

    // Check for native support
    const storageAvailable = (type) => {
        try {
            const storage = window[type];
            const x = '__storage_test__';
            storage.setItem(x, x);
            storage.removeItem(x);
            return true;
        } catch (e) {
            return false;
        }
    };

    /**
   * Creates new cookie or removes cookie with negative expiration
   * @param  cookieKey       The key or identifier for the store
   * @param  cookieValue     Contents of the store
   * @param  exp       Expiration - creation defaults to 30 days
   */

    const createCookie = (cookieKey, cookieValue, exp) => {
        const date = new Date();
        date.setTime(date.getTime() + (exp * 24 * 60 * 60 * 1000));
        const expires = '; expires=' + date.toGMTString();
        document.cookie = cookieKey + '=' + cookieValue + expires + '; path=/';
    };

    /**
     * Returns contents of cookie
     * @param  cookieKey       The key or identifier for the store
     */

    const readCookie = (cookieKey) => {
        const nameEQ = cookieKey + '=';
        const ca = document.cookie.split(';');
        for (let i = 0, max = ca.length; i < max; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
        return null;
    };

    if (storageAvailable('localStorage')) {
        // Yippee! We can use localStorage awesomeness
        lsSupport = true;
    }

    // If value is detected, set new or modify store
    if (typeof value !== 'undefined' && value !== null) {
        // Convert object values to JSON
        if (typeof value === 'object') {
            value = JSON.stringify(value); // eslint-disable-line no-param-reassign
        }
        // Set the store
        if (lsSupport) { // Native support
            localStorage.setItem(key, value);
        } else { // Use Cookie
            createCookie(key, value, cookieConfig.expiration);
        }
    }

    // No value supplied, return value
    if (typeof value === 'undefined') {
        let data;
        // Get value
        if (lsSupport) { // Native support
            data = localStorage.getItem(key);
        } else { // Use cookie
            data = readCookie(key);
        }
        return data;
    }

    // Null specified, remove store
    if (value === null) {
        if (lsSupport) { // Native support
            localStorage.removeItem(key);
        } else { // Use cookie
            createCookie(key, '', -1);
        }
    }

    return null;
};

export default appStorage;
